import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { KeycloakTokenParsed } from "keycloak-js";
import { toast } from 'react-toastify';

import { createProjectActionItems, updateProjectActionItems, deleteProjectActionItems } from "../../api/actionItems";
import { Pagination } from "../common/Pagination";
import { ACTION_ITEM_SORT_FIELDS, applySortClick, DEFAULT_ITEM_SORT, isDefaultItemSort, ItemListQuery, LIST_DEFAULT_SIZE, SortParam, sortIndicator } from "../../types/pagination";
import { PAGE_SIZE_OPTIONS } from "../../constants/pagination";
import { getStoredValue, storeValue } from "../../utils/localStorage";
import { hasStagedChanges } from "../common/stagedChanges";
import { SortControl } from "../common/SortControl";
import { clearStagedDraft, getStagedDraft, setStagedDraft } from "../../utils/stagedDrafts";

const ACTION_ITEMS_PAGE_SIZE_KEY = 'actionItems_pageSize';
const ACTION_ITEMS_SORT_KEY = 'actionItems_sortKeys';

const isStoredSortKeys = (value: unknown): value is SortParam[] =>
  Array.isArray(value) &&
  value.length >= 1 &&
  value.length <= 3 &&
  value.every((key) =>
    typeof key === 'object' && key !== null &&
    (ACTION_ITEM_SORT_FIELDS as readonly string[]).includes((key as SortParam).field) &&
    ((key as SortParam).direction === 'asc' || (key as SortParam).direction === 'desc')
  );

/** Sort fields offered in the sort panel. */
const ACTION_ITEM_SORT_OPTIONS = [
  { value: 'created_at', label: 'Created' },
  { value: 'start_date', label: 'Start date' },
  { value: 'due_date', label: 'Due date' },
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'risk', label: 'Risk' },
];

/** Label for a sort key in the active-sort chip. */
const SORT_FIELD_LABELS: Record<string, string> = {
  created_at: 'Created',
  start_date: 'Start',
  due_date: 'Due',
  status: 'Status',
  priority: 'Priority',
};
import { deleteActionItemDocument } from "../../api/documents";

import { useProjectActionItemsStore, useProjectActionItemsPage } from "../../state/projects";
import { STATUS_LABELS } from "../../constants/project";
import { useUsersPage } from "../../state/users";
import { Avatar } from '../Avatar';
import { LoadingSpinner } from '../LoadingSpinner';
import { ActionItemsDocumentAttachment } from './ActionItemsDocumentAttachment';
import { AddActionItemModal } from './AddActionItemModal';
import { ItemUpdatesModal } from './ItemUpdatesModal';

import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaTrash,
  FaCheckCircle,
  FaThList,
  FaTh,
  FaLink
} from 'react-icons/fa';
import { ActionItemsKanbanBoard } from './ActionItemsKanbanBoard';

import {
  getTableHeaderStyle,
  getFormSelectStyle,
  getFormSelectContainerStyle,
  DesignSystem,
} from '../../styles/designSystem';

import { useKeycloak } from '@react-keycloak/web';
import { API_URL } from '../../config/api';
import { useCanEditTaskOwner } from '../../context/projectAccess';

interface TabActionItemsProps {
  projectId: string | undefined;
  token: string | undefined;
  projectData: ProjectInterface,
  tokenParsed: KeycloakTokenParsed | undefined;
  onProjectUpdate?: () => Promise<void>;
  actionItems: ProjectActionItem[];
  highlightedItemId?: string | null;
  readOnly?: boolean;
}

// Action Item row component in ProjectPlan2 style
// Exported for unit testing the row's inline-edit behavior in isolation.
export const ActionItemRow = ({
  actionItem,
  projectData,
  onSubmit,
  onDelete,
  onProjectUpdate,
  isNew = false,
  isHighlighted = false,
  canDelete = false,
  isExpanded = false,
  onToggleExpand,
}: {
  actionItem: ProjectActionItem,
  projectData: ProjectInterface,
  onSubmit: Function,
  onDelete: (actionItemId: string) => void,
  onProjectUpdate?: () => Promise<void>,
  isNew?: boolean,
  isHighlighted?: boolean,
  canDelete?: boolean,
  isExpanded?: boolean,
  onToggleExpand?: () => void,
}) => {
  // Changing a task's owner requires tasks:update (FD2-1129)
  const canEditOwner = useCanEditTaskOwner();
  // Unsaved edits survive a tab switch, which unmounts the row; see utils/stagedDrafts.
  const draftKey = `action-item:${actionItem.id}`;
  const [form, setForm] = useState<ProjectActionItem>(() => getStagedDraft<ProjectActionItem>(draftKey) ?? { ...actionItem });
  const [isSaving, setIsSaving] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  // Last saved copy of the item. Edits are staged in `form` until Save; Cancel restores this copy.
  const [savedForm, setSavedForm] = useState({ ...actionItem });
  const rowRef = React.useRef<HTMLDivElement>(null);
  const isProdEnv = typeof window !== 'undefined' && window.location.hostname.includes('app.finderz.ai');

  // Ref to always hold the latest form state, preventing race conditions
  // when multiple fields are changed quickly before saves complete
  const formRef = useRef({ ...actionItem });
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const { addUserToProject } = useUsersPage();
  const { keycloak: { token: authToken } } = useKeycloak();
  // Get store function to update single action item
  const updateSingleActionItem = useProjectActionItemsStore(state => state.updateSingleActionItem);
  // Get reload function (like Documents tab does)
  const { loadProjectActionItemsPage } = useProjectActionItemsPage(projectData?.id || '');

  // A reload or store update of this item must not discard edits the user has staged.
  const savedFormRef = useRef(savedForm);
  savedFormRef.current = savedForm;
  useEffect(() => {
    setSavedForm({ ...actionItem });
    setForm(prev => (hasStagedChanges(prev, savedFormRef.current) ? prev : { ...actionItem }));
  }, [actionItem]);

  // Scroll to highlighted item
  useEffect(() => {
    if (isHighlighted && rowRef.current) {
      setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isHighlighted]);

  // Serialized save: each save waits for the previous one to finish,
  // and always reads from formRef to capture the latest field values.
  const saveChanges = useCallback((updatedForm: any, reloadAfterSave = false) => {
    // Update the ref immediately so subsequent saves see this change
    formRef.current = { ...updatedForm };

    saveQueueRef.current = saveQueueRef.current.then(async () => {
      // Always use the latest ref value — it may have been updated
      // by another field change while we were queued
      const latestForm = { ...formRef.current };
      setIsSaving(true);
      try {
        await onSubmit(latestForm);
        setSavedForm({ ...latestForm });
        // Update the store so data persists across tab switches
        if (projectData?.id) {
          updateSingleActionItem(projectData.id, latestForm);
        }
        // Sync local form state with what was actually saved
        setForm({ ...latestForm });
        // Reload all action items from API (like Documents tab does)
        if (reloadAfterSave) {
          setTimeout(() => loadProjectActionItemsPage({ page: 1 }), 500);
        }
      } finally {
        setIsSaving(false);
      }
    });
  }, [onSubmit, projectData?.id, updateSingleActionItem, loadProjectActionItemsPage]);

  const handleFieldChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Stages changes in the row. Nothing is sent to the backend until the user presses Save.
  const stageChange = (updates: Record<string, any>) => {
    setForm(prev => ({ ...prev, ...updates }));
  };

  // Blur only stages the value; a blur without a change does not send a request.
  const handleFieldBlur = (field: string, value: any) => {
    stageChange({ [field]: value });
  };

  // Any difference from the saved copy means unsaved changes.
  const isDirty = hasStagedChanges(form, savedForm);

  // Keep the draft in step with the row: stored while edits are unsaved, cleared once they are saved or cancelled.
  useEffect(() => {
    if (isDirty) setStagedDraft(draftKey, form);
    else clearStagedDraft(draftKey);
  }, [isDirty, form, draftKey]);

  // Saves the form state, so edits made in any input (including the description) are included.
  const saveStagedChanges = () => saveChanges(form);

  const discardStagedChanges = () => {
    setForm({ ...savedForm });
  };

  const handleUserAdd = async (field: 'owner_id' | 'assigned_to', userId: string) => {
    // The "Unassigned" option submits an empty value. Persist it as null: the API
    // types both fields as UUID-or-null and rejects an empty string as a bad UUID.
    if (!userId) {
      handleFieldChange(field, null);
      handleFieldBlur(field, null);
      return;
    }

    const user = projectData?.users?.find(u => u.id === userId);
    if (user) {
      const userInProject = !!projectData.users?.find((u: ProjectUserInterface) => u.id === userId);
      if (!userInProject) {
        await addUserToProject(projectData.id || '', user);
      }
      handleFieldChange(field, userId);
      handleFieldBlur(field, userId);
    }
  };



  return (
    <>
      {isDirty && (
        <div data-testid="action-item-unsaved-bar" className="flex items-center justify-end gap-2 px-6 py-1.5 bg-amber-50 border-b border-amber-100">
          <span className="text-[11px] text-amber-700 mr-auto">Unsaved changes</span>
          <button
            data-testid="action-item-cancel"
            onClick={(e) => { e.stopPropagation(); discardStagedChanges(); }}
            disabled={isSaving}
            className="px-2 py-1 text-xs font-medium text-[#444] bg-white border border-[#eaeaea] rounded hover:bg-[#fafafa] transition-colors duration-150 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="action-item-save"
            onClick={(e) => { e.stopPropagation(); saveStagedChanges(); }}
            disabled={isSaving}
            className="px-2 py-1 text-xs font-medium text-white bg-[#171717] rounded hover:bg-[#333] transition-colors duration-150 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
      <div
        ref={rowRef}
        data-testid="action-item-row"
        data-item-id={actionItem.id}
        className={`group transition-all duration-200 ${
          isNew ? 'bg-[#f5f5f5]/30' : ''
        } ${
          isHighlighted ? 'bg-yellow-50/50 border-l-4 border-yellow-400' : ''
        } ${isExpanded ? 'bg-[#fafafa]/40' : 'hover:bg-[#fafafa]/50'} ${
          isDirty ? 'shadow-[inset_3px_0_0_#f59e0b]' : ''
        }`}
      >
      {/* Main Row — all fields inline-editable */}
      <div className="flex items-center px-5 py-2.5 relative" style={{ borderBottom: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>

        {/* Left: expand + indicator + name */}
        <div className="flex items-center flex-1 min-w-0">
          <button
            onClick={() => onToggleExpand?.()}
            className="mr-2 p-1 rounded hover:bg-[#eaeaea]/60 transition-colors relative z-10 flex-shrink-0"
          >
            <FaChevronDown className={`w-2.5 h-2.5 text-[#aaa] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>

          {/* Status checkbox-style indicator */}
          <div className="mr-2.5 relative z-10 flex-shrink-0 cursor-pointer" onClick={(e) => {
            e.stopPropagation();
            const next = form.status === 'COMPLETED' ? 'NOT_STARTED' : 'COMPLETED';
            handleFieldChange('status', next);
            stageChange({ status: next });
          }} title={form.status === 'COMPLETED' ? 'Mark incomplete' : 'Mark complete'}>
            <div className="relative">
              {isSaving && (
                <div className="absolute -inset-1 rounded-full border-2 border-transparent border-t-indigo-400 animate-spin" />
              )}
              {form.status === 'COMPLETED' ? (
                <FaCheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <div className={`w-4 h-4 rounded-full border-2 ${
                  (() => {
                    // Use the highest severity between priority and risk
                    const severityMap: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
                    const pSev = severityMap[form.priority?.toLowerCase() || ''] || 0;
                    const rSev = severityMap[form.risks?.toLowerCase() || ''] || 0;
                    const maxSev = Math.max(pSev, rSev);
                    if (maxSev >= 3) return 'border-red-400';
                    if (maxSev === 2) return 'border-yellow-400';
                    if (maxSev === 1) return 'border-green-400';
                    return 'border-[#eaeaea]';
                  })()
                } hover:border-green-400 transition-colors`} />
              )}
            </div>
          </div>

          {/* Name — inline editable */}
          <input
            type="text"
            value={form.name || ''}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            onBlur={(e) => handleFieldBlur('name', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            maxLength={255}
            className={`flex-1 min-w-0 text-sm bg-transparent border-0 focus:outline-none px-1 py-0.5 rounded hover:bg-white focus:bg-white focus:ring-0 focus:border-[#888] transition-all truncate relative z-10 ${
              form.status === 'COMPLETED' ? 'line-through text-[#aaa]' : 'text-[#171717]'
            }`}
            placeholder="Action item name..."
            style={{ fontFamily: DesignSystem.typography.body.fontFamily }}
          />
        </div>

        {/* Right: fixed inline-editable fields */}
        <div className="flex items-center flex-shrink-0 relative z-10 gap-2">
          {/* Status */}
          <div className="w-[82px]">
            <select
              value={form.status || 'NOT_STARTED'}
              onChange={(e) => {
                handleFieldChange('status', e.target.value);
                stageChange({ status: e.target.value });
              }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full text-[11px] font-medium rounded-full px-2 py-1 border-0 cursor-pointer no-arrow focus:outline-none focus:ring-0 focus:border-[#888] transition-colors ${
                form.status === 'COMPLETED' ? 'bg-green-50 text-green-700' :
                form.status === 'IN_PROGRESS' ? 'bg-[#f5f5f5] text-[#171717]' :
                form.status === 'ON_HOLD' ? 'bg-yellow-50 text-yellow-700' :
                'bg-[#f5f5f5] text-[#555]'
              }`}

            >
              <option value="NOT_STARTED">Not Started</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="ON_HOLD">On Hold</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>

          {/* Owner */}
          <div className="w-11 flex justify-center relative group/owner">
            <div title={(() => {
              const user = projectData?.users?.find(u => u.id === form.owner_id);
              return user ? `Owner: ${user.first_name} ${user.last_name}` : 'Owner: Unassigned';
            })()}>
              <Avatar
                firstName={projectData?.users?.find(u => u.id === form.owner_id)?.first_name}
                lastName={projectData?.users?.find(u => u.id === form.owner_id)?.last_name}
                email={projectData?.users?.find(u => u.id === form.owner_id)?.email}
                size={26}
              />
            </div>
            {canEditOwner && (
              <select
                aria-label="Owner"
                value={form.owner_id || ''}
                onChange={(e) => { e.stopPropagation(); handleUserAdd('owner_id', e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                className="absolute inset-0 opacity-0 cursor-pointer z-10"
              >
                <option value="">Unassigned</option>
                {projectData?.users?.map((user) => (
                  <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Assignee */}
          <div className="w-11 flex justify-center relative group/assignee">
            <div title={(() => {
              const user = projectData?.users?.find(u => u.id === form.assigned_to);
              return user ? `Assignee: ${user.first_name} ${user.last_name}` : 'Assignee: Unassigned';
            })()}>
              <Avatar
                firstName={projectData?.users?.find(u => u.id === form.assigned_to)?.first_name}
                lastName={projectData?.users?.find(u => u.id === form.assigned_to)?.last_name}
                email={projectData?.users?.find(u => u.id === form.assigned_to)?.email}
                size={26}
              />
            </div>
            <select
              aria-label="Assignee"
              value={form.assigned_to || ''}
              onChange={(e) => { e.stopPropagation(); handleUserAdd('assigned_to', e.target.value); }}
              onClick={(e) => e.stopPropagation()}
              className="absolute inset-0 opacity-0 cursor-pointer z-10"
            >
              <option value="">Unassigned</option>
              {projectData?.users?.map((user) => (
                <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div className="w-[100px]">
            <input
              type="date"
              value={form.due_date ? new Date(form.due_date).toISOString().split('T')[0] : ''}
              onChange={(e) => {
                const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                handleFieldChange('due_date', value);
              }}
              onBlur={(e) => {
                const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                handleFieldBlur('due_date', value);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full text-xs text-[#555] bg-transparent border-0 rounded px-1 py-0.5 hover:bg-white focus:bg-white focus:outline-none focus:ring-0 focus:border-[#888] cursor-pointer transition-all"
            />
          </div>

          {/* Created (read-only) */}
          <div className="w-[100px] text-xs text-[#555] px-1 py-0.5">
            {form.created_at ? new Date(form.created_at).toLocaleDateString() : ''}
          </div>

          {/* Priority */}
          <div className="w-[62px]">
            <select
              value={form.priority || 'LOW'}
              onChange={(e) => {
                handleFieldChange('priority', e.target.value);
                stageChange({ priority: e.target.value });
              }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full text-[11px] font-medium border-0 rounded px-1.5 py-1 cursor-pointer no-arrow focus:outline-none focus:ring-0 focus:border-[#888] transition-colors ${
                form.priority?.toLowerCase() === 'high' ? 'text-red-600 bg-red-50' :
                form.priority?.toLowerCase() === 'medium' ? 'text-yellow-700 bg-yellow-50' :
                'text-green-700 bg-green-50'
              }`}

            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>
          </div>

          {/* Risk */}
          <div className="w-[62px]">
            <select
              value={form.risks || ''}
              onChange={(e) => {
                handleFieldChange('risks', e.target.value);
                stageChange({ risks: e.target.value });
              }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full text-[11px] font-medium border-0 rounded px-1.5 py-1 cursor-pointer no-arrow focus:outline-none focus:ring-0 focus:border-[#888] transition-colors ${
                form.risks?.toLowerCase() === 'critical' ? 'text-red-700 bg-red-50' :
                form.risks?.toLowerCase() === 'high' ? 'text-orange-700 bg-orange-50' :
                form.risks?.toLowerCase() === 'medium' ? 'text-yellow-700 bg-yellow-50' :
                form.risks?.toLowerCase() === 'low' ? 'text-green-700 bg-green-50' :
                'text-[#888] bg-[#fafafa]'
              }`}

            >
              <option value="">None</option>
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>

          {/* Doc indicator — click to download with auth */}
          <div className="w-7 flex justify-center flex-shrink-0">
            {form.document && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    let downloadUrl = form.document!;
                    if (!downloadUrl && form.file_name) {
                      downloadUrl = `${API_URL}/action_items/${form.project_id}/items/${form.id}/document/${encodeURIComponent(form.file_name)}`;
                    }
                    // Encode non-ASCII characters (e.g. Cyrillic filenames) in the URL path
                    // Split URL, encode each path segment, reassemble
                    try {
                      const urlObj = new URL(downloadUrl);
                      urlObj.pathname = urlObj.pathname.split('/').map(seg => encodeURIComponent(decodeURIComponent(seg))).join('/');
                      downloadUrl = urlObj.href;
                    } catch (_) { /* keep original URL */ }
                    const response = await fetch(downloadUrl, {
                      method: 'GET',
                      headers: { 'authorization': 'Bearer ' + authToken }
                    });
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const ext = (form.file_name || '').toLowerCase();
                    if (ext.endsWith('.pdf') || ext.match(/\.(png|jpg|jpeg|gif|svg|webp)$/)) {
                      window.open(url, '_blank');
                    } else {
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = form.file_name || 'document';
                      a.click();
                    }
                    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                  } catch (err) {
                    console.error('Failed to download document:', err, 'URL:', form.document);
                    toast.error('Failed to open document');
                  }
                }}
                className="text-[#aaa] hover:text-[#171717] transition-colors"
                title={form.file_name || 'Open attached document'}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
            )}
          </div>

          {/* Copy Link */}
          <div className="w-6 flex justify-center flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const url = `${window.location.origin}/projects/${projectData.id}?tab=action-items&item=${actionItem.id}`;
                navigator.clipboard.writeText(url).then(() => {
                  toast.success('Link copied to clipboard');
                });
              }}
              className="p-0.5 rounded text-[#ccc] hover:text-[#171717] hover:bg-[#fafafa] opacity-0 group-hover:opacity-100 transition-all"
              title="Copy link"
            >
              <FaLink className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded: all fields for editing + description + documents + actions */}
      {isExpanded && (
        <div className="px-5 pb-4 pt-2" style={{ paddingLeft: '3.25rem' }}>
          {/* Editable fields grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            {/* Status */}
            <div>
              <label className="block text-[11px] font-medium text-[#888] mb-1">Status</label>
              <select
                value={form.status || 'NOT_STARTED'}
                onChange={(e) => {
                  handleFieldChange('status', e.target.value);
                  stageChange({ status: e.target.value });
                }}
                className={`w-full text-xs font-medium rounded-md px-2.5 py-1.5 border border-[#eaeaea] cursor-pointer no-arrow focus:outline-none focus:ring-0 focus:border-[#888] focus:border-[#888] transition-colors ${
                  form.status === 'COMPLETED' ? 'bg-green-50 text-green-700' :
                  form.status === 'IN_PROGRESS' ? 'bg-[#f5f5f5] text-[#171717]' :
                  form.status === 'ON_HOLD' ? 'bg-yellow-50 text-yellow-700' :
                  'bg-[#fafafa] text-[#555]'
                }`}
  
                >
                <option value="NOT_STARTED">Not Started</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="ON_HOLD">On Hold</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-[11px] font-medium text-[#888] mb-1">Priority</label>
              <select
                value={form.priority || 'LOW'}
                onChange={(e) => {
                  handleFieldChange('priority', e.target.value);
                  stageChange({ priority: e.target.value });
                }}
                className={`w-full text-xs font-medium rounded-md px-2.5 py-1.5 border border-[#eaeaea] cursor-pointer no-arrow focus:outline-none focus:ring-0 focus:border-[#888] focus:border-[#888] transition-colors ${
                  form.priority?.toLowerCase() === 'high' ? 'bg-red-50 text-red-600' :
                  form.priority?.toLowerCase() === 'medium' ? 'bg-yellow-50 text-yellow-700' :
                  'bg-green-50 text-green-700'
                }`}
  
                >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </div>

            {/* Risk */}
            <div>
              <label className="block text-[11px] font-medium text-[#888] mb-1">Risk</label>
              <select
                value={form.risks || ''}
                onChange={(e) => {
                  handleFieldChange('risks', e.target.value);
                  stageChange({ risks: e.target.value });
                }}
                className={`w-full text-xs font-medium rounded-md px-2.5 py-1.5 border border-[#eaeaea] cursor-pointer no-arrow focus:outline-none focus:ring-0 focus:border-[#888] focus:border-[#888] transition-colors ${
                  form.risks?.toLowerCase() === 'critical' ? 'bg-red-50 text-red-700' :
                  form.risks?.toLowerCase() === 'high' ? 'bg-orange-50 text-orange-700' :
                  form.risks?.toLowerCase() === 'medium' ? 'bg-yellow-50 text-yellow-700' :
                  form.risks?.toLowerCase() === 'low' ? 'bg-green-50 text-green-700' :
                  'bg-[#fafafa] text-[#888]'
                }`}
  
                >
                <option value="">None</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>

            {/* Owner */}
            <div>
              <label className="block text-[11px] font-medium text-[#888] mb-1">Owner</label>
              <select
                value={form.owner_id || ''}
                onChange={(e) => handleUserAdd('owner_id', e.target.value)}
                disabled={!canEditOwner}
                title={!canEditOwner ? 'You do not have permission to change the owner' : undefined}
                className={`w-full text-xs rounded-md px-2.5 py-1.5 border border-[#eaeaea] no-arrow focus:outline-none focus:ring-0 focus:border-[#888] transition-colors text-[#444] ${canEditOwner ? 'bg-white cursor-pointer' : 'bg-[#f5f5f5] cursor-not-allowed opacity-70'}`}
                >
                <option value="">Unassigned</option>
                {projectData?.users?.map((user) => (
                  <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
                ))}
              </select>
            </div>

            {/* Assignee */}
            <div>
              <label className="block text-[11px] font-medium text-[#888] mb-1">Assignee</label>
              <select
                value={form.assigned_to || ''}
                onChange={(e) => handleUserAdd('assigned_to', e.target.value)}
                className="w-full text-xs rounded-md px-2.5 py-1.5 border border-[#eaeaea] bg-white cursor-pointer no-arrow focus:outline-none focus:ring-0 focus:border-[#888] focus:border-[#888] transition-colors text-[#444]"
  
                >
                <option value="">Unassigned</option>
                {projectData?.users?.map((user) => (
                  <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
                ))}
              </select>
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-[11px] font-medium text-[#888] mb-1">Due Date</label>
              <input
                type="date"
                value={form.due_date ? new Date(form.due_date).toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                  handleFieldChange('due_date', value);
                }}
                onBlur={(e) => {
                  const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                  handleFieldBlur('due_date', value);
                }}
                className="w-full text-xs text-[#444] rounded-md px-2.5 py-1.5 border border-[#eaeaea] bg-white focus:outline-none focus:ring-0 focus:border-[#888] focus:border-[#888] cursor-pointer transition-colors"
              />
            </div>
          </div>

          {/* Description */}
          <textarea
            data-testid="action-item-description"
            value={form.description || ''}
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            onClick={(e) => e.stopPropagation()}
            className={`w-full text-sm text-[#555] bg-white border rounded-lg px-3 py-2 hover:border-[#eaeaea] focus:outline-none focus:ring-0 focus:border-[#888] resize-y min-h-[60px] transition-all ${
              (form.description || '') !== (actionItem.description || '')
                ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-200'
                : 'border-[#eaeaea] focus:border-blue-400'
            }`}
            placeholder="Add a description..."
            style={{ fontFamily: DesignSystem.typography.body.fontFamily }}
          />

          {/* Bottom bar: document + save/discard + actions */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <ActionItemsDocumentAttachment
                projectId={form.project_id || ''}
                itemId={form.id || ''}
                documentUrl={form.document ?? null}
                fileName={form.file_name ?? null}
                onUpdate={onProjectUpdate}
                onDocumentChange={(docUrl, docName) => {
                  const documentFields = { document: docUrl || null, file_name: docName || null };
                  // The document endpoint already stores the link (and clears it on delete), so no
                  // update request is needed. Only the local copies are brought in line with it.
                  stageChange(documentFields);
                  const savedAfterDocument = { ...savedForm, ...documentFields };
                  setSavedForm(savedAfterDocument);
                  if (projectData?.id) updateSingleActionItem(projectData.id, savedAfterDocument);
                }}
              />
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 ml-3">
              {!isProdEnv && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowUpdates(true); }}
                  className="px-2 py-1 text-xs text-[#888] hover:bg-[#fafafa] hover:text-[#171717] rounded transition-colors duration-150"
                >
                  Updates
                </button>
              )}
              <button
                onClick={() => {
                  const url = `${window.location.origin}/projects/${projectData.id}?tab=action-items&item=${actionItem.id}`;
                  navigator.clipboard.writeText(url).then(() => toast.success('Link copied'));
                }}
                className="flex items-center gap-1 px-2 py-1 text-xs text-[#888] hover:text-[#171717] hover:bg-[#fafafa] rounded transition-colors duration-150"
              >
                <FaLink className="w-2.5 h-2.5" /> Link
              </button>
              {canDelete && (
                <button
                  data-testid="action-item-delete"
                  onClick={() => onDelete(actionItem.id!)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-[#888] hover:text-[#dc2626] hover:bg-red-50 rounded transition-colors"
                >
                  <FaTrash className="w-2.5 h-2.5" /> Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    {!isProdEnv && (
      <ItemUpdatesModal
        projectId={form.project_id || ''}
        itemId={form.id || ''}
        isOpen={showUpdates}
        onClose={() => setShowUpdates(false)}
      />
    )}
    </>
  );
};

export const TabActionItems = ({
  token,
  projectData,
  projectId,
  onProjectUpdate: onProjectUpdateProp,
  actionItems,
  highlightedItemId,
  readOnly,
}: TabActionItemsProps) => {
  const { keycloak: { idTokenParsed } } = useKeycloak();
  // Every save or delete goes through here: reload the project, then refetch this tab's page and counts.
  const [itemsRefreshKey, setItemsRefreshKey] = useState(0);
  const onProjectUpdate = useCallback(async () => {
    await onProjectUpdateProp?.();
    setItemsRefreshKey((key) => key + 1);
  }, [onProjectUpdateProp]);
  // Resolve current user ID from project users by email
  const currentUserId = useMemo(() => {
    const currentUser = projectData?.users?.find(user => user.email === idTokenParsed?.email);
    return currentUser?.id || '';
  }, [projectData?.users, idTokenParsed?.email]);

  // Add cleanup flag to prevent DOM operations after unmount. Must be a ref,
  // not state: under React StrictMode's dev-only mount/cleanup/remount
  // simulation, a state-based flag set `true` by the cleanup never gets reset
  // back to `false`, permanently disabling every effect gated on it.
  const isUnmountedRef = useRef(false);
  // View mode (table or kanban) persisted in localStorage
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>(() => {
    const saved = localStorage.getItem(`actionItems_viewMode_${projectId}`);
    return (saved === 'kanban' ? 'kanban' : 'table');
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      localStorage.setItem(`actionItems_viewMode_${projectId}`, viewMode);
    }
  }, [viewMode, projectId]);

  useEffect(() => {
    if (actionItems && actionItems.length >= 0) {
      setIsLoading(false);
    }
  }, [actionItems]);

  // Filters and search (ProjectPlan2 style)
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [showAddActionItem, setShowAddActionItem] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Up to three sort keys, applied in order by the backend. Default: created_at:desc. Remembered across visits.
  const [sortKeys, setSortKeys] = useState<SortParam[]>(() =>
    getStoredValue(ACTION_ITEMS_SORT_KEY, isStoredSortKeys, DEFAULT_ITEM_SORT)
  );
  useEffect(() => {
    storeValue(ACTION_ITEMS_SORT_KEY, sortKeys);
  }, [sortKeys]);
  // Only one item can be expanded at a time
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // Scroll detection
  useEffect(() => {
    if (isUnmountedRef.current) return;

    const handleScroll = () => {
      if (!isUnmountedRef.current) {
      setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup effect to prevent DOM operations after unmount. Reset on setup
  // too, so StrictMode's mount/cleanup/remount simulation doesn't leave this
  // permanently `true` after the initial mount.
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  // Listen for document deletion events from Documents tab
  useEffect(() => {
    console.log('🎧 [TabActionItems] Setting up event listener for actionItemDocumentDeleted');
    console.log('🎧 [TabActionItems] Current projectId:', projectId);
    
    const handleDocumentDeleted = (event: CustomEvent) => {
      console.log('📢 [TabActionItems] Received actionItemDocumentDeleted event');
      console.log('📢 [TabActionItems] Event detail:', event.detail);
      console.log('📢 [TabActionItems] Current projectId:', projectId);
      console.log('📢 [TabActionItems] Event projectId:', event.detail.projectId);
      
      if (projectId && event.detail.projectId === projectId) {
        console.log('🔄 [TabActionItems] Project IDs match, will refresh after delay');
        // Add delay to allow backend to process the deletion and update relationships
        setTimeout(() => {
          console.log('🔄 [TabActionItems] Refreshing action items now');
          if (onProjectUpdate) {
            onProjectUpdate();
          }
          console.log('✅ [TabActionItems] Refresh triggered');
        }, 2000); // 2 second delay
      } else {
        console.log('⚠️ [TabActionItems] Project IDs do not match or projectId is undefined');
      }
    };

    window.addEventListener('actionItemDocumentDeleted', handleDocumentDeleted as EventListener);
    console.log('✅ [TabActionItems] Event listener added');
    
    return () => {
      console.log('🧹 [TabActionItems] Removing event listener');
      window.removeEventListener('actionItemDocumentDeleted', handleDocumentDeleted as EventListener);
    };
  }, [projectId, onProjectUpdate]);

  // `silent` saves without a message, for changes that already show their own (e.g. a document upload).
  const updateActionItemHandler = async (form: ProjectActionItem, options: { silent?: boolean } = {}) => {
    try {
      if (token && projectId) {
        await updateProjectActionItems(token, projectId, form.id, form);
        if (!options.silent) toast.success('Action item updated successfully');
        
        // Don't call onProjectUpdate() after successful update
        // The local data is already up-to-date from the form
        // Calling onProjectUpdate() would reload all project data and overwrite 
        // any unsaved changes in other fields that the user is currently editing
        // Only reload on error (handled in catch block) or when adding/deleting items
      }
    } catch (error: any) {
      console.error('Error updating action item:', error);
      toast.error(error.message || 'Failed to update action item');
      
      // On error, reload data to ensure consistency
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    }
  };

  const handleAddActionItem = async (actionItemData: any) => {
    try {
      if (token && projectId) {
        await createProjectActionItems(token, projectId, actionItemData);
        toast.success('Action item added successfully');
        setShowAddActionItem(false);
        
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error: any) {
      console.error('Error adding action item:', error);
      toast.error(error.message || 'Failed to add action item');
    }
  };

  const deleteActionItemHandler = async (actionItemId: string) => {
    if (!token || !projectId) return;

    if (!window.confirm('Are you sure you want to delete this action item?')) return;
    
    try {
      // Find the action item to check if it has a document
      const itemForDelete = actionItems.find(item => item.id === actionItemId);
      
      // If action item has a document, delete it first
      if (itemForDelete?.document) {
        console.log(`Deleting document for action item ${actionItemId} before deleting the action item itself`);
        try {
          await deleteActionItemDocument(token, projectId, actionItemId);
          console.log('Document deleted successfully');
        } catch (docError) {
          console.error('Failed to delete action item document:', docError);
          // Continue with action item deletion even if document deletion fails
          toast.warning('Action item document could not be deleted, but will delete the action item');
        }
      }
      
      // Delete the action item itself
      await deleteProjectActionItems(token, projectId, actionItemId);
      toast.success('Action item deleted successfully');
      
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error: any) {
      console.error('Error deleting action item:', error);
      toast.error(error.message || 'Failed to delete action item');
    }
  };

  // Paging, search, filters and sort are applied by the server. The page of items is what `actionItems` holds.
  const [page, setPage] = useState(1);
  // Rows per page is remembered across visits, like the download log pager.
  const [pageSize, setPageSize] = useState<number>(() =>
    getStoredValue(ACTION_ITEMS_PAGE_SIZE_KEY, (value): value is number => typeof value === 'number' && PAGE_SIZE_OPTIONS.includes(value), LIST_DEFAULT_SIZE)
  );
  const handleSizeChange = (size: number) => {
    setPageSize(size);
    storeValue(ACTION_ITEMS_PAGE_SIZE_KEY, size);
  };
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { loadProjectActionItemsPage } = useProjectActionItemsPage(projectId || '');
  const actionItemsPagination = useProjectActionItemsStore((state) => state.pagination);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Any filter, sort or size change starts again from the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, userFilter, priorityFilter, riskFilter, sortKeys, pageSize]);

  const actionItemQuery = useMemo<ItemListQuery>(() => ({
    page,
    size: pageSize,
    search: debouncedSearch || undefined,
    statusValues: statusFilter !== 'all' ? [statusFilter] : undefined,
    ownerIds: userFilter !== 'all' ? [userFilter] : undefined,
    priorityValues: priorityFilter !== 'all' ? [priorityFilter] : undefined,
    sort: sortKeys,
    risk: riskFilter !== 'all' ? riskFilter : undefined,
  }), [page, pageSize, debouncedSearch, statusFilter, userFilter, priorityFilter, riskFilter, sortKeys]);

  useEffect(() => {
    if (!projectId) return;
    loadProjectActionItemsPage(actionItemQuery).catch((error: unknown) => {
      console.error('Failed to load action items:', error);
      toast.error('Failed to load action items');
    });
  }, [actionItemQuery, projectId, itemsRefreshKey]);


  const filteredActionItems = actionItems;

  // Already sorted by the server (status and priority use their logical order).
  const sortedActionItems = filteredActionItems;

  // Plain click: sort by this column (click again to flip). Shift-click: add it as a secondary key.
  const toggleSort = (field: 'status' | 'due_date' | 'priority' | 'created_at' | 'risk', additive: boolean) => {
    setSortKeys((keys) => applySortClick(keys, field, additive));
  };

  return (
    <div className="min-h-screen" style={{ position: 'relative', zIndex: 1 }}>
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div 
        className="sticky top-0"
        style={{ 
          zIndex: 10,
          position: 'sticky',
          backgroundColor: 'white'
        }}
      >
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow duration-200 ${isScrolled ? 'shadow-[0_4px_12px_rgba(0,0,0,0.06)]' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#888] w-3.5 h-3.5" />
                    <input
                      type="text"
                      data-testid="action-items-search"
                      placeholder="Search action items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 w-80 text-[13px] text-[#444] bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0 focus:bg-white transition-all duration-150 placeholder-[#aaa]"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-[12px]">
                    <span className="text-[#888]">
                      {actionItemsPagination.total} items
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  <SortControl
                    keys={sortKeys}
                    fields={ACTION_ITEM_SORT_OPTIONS}
                    onChange={setSortKeys}
                    onReset={() => setSortKeys(DEFAULT_ITEM_SORT)}
                  />
                  {/* Filters */}
                  <button
                    data-testid="action-items-filter-toggle"
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-3.5 py-2 text-[13px] font-medium rounded-lg transition-all duration-150 ${
                      showFilters
                        ? 'bg-[#171717] text-white border border-[#171717]'
                        : 'bg-white text-[#444] hover:bg-[#fafafa] border border-[#eaeaea]'
                    }`}
                  >
                    <FaFilter className="w-3 h-3" />
                    <span>Filter</span>
                    {(statusFilter !== 'all' || userFilter !== 'all' || priorityFilter !== 'all') && (
                      <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-white text-[#171717] rounded-full font-semibold">
                        {[statusFilter, userFilter, priorityFilter].filter(f => f !== 'all').length}
                      </span>
                    )}
                  </button>

                  {/* View Toggle */}
                  <div className="flex items-center bg-[#f5f5f5] rounded-lg p-0.5 border border-[#eaeaea]">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 text-[13px] font-medium rounded-md transition-all duration-150 ${
                        viewMode === 'table'
                          ? 'bg-white text-[#171717] shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-[#eaeaea]'
                          : 'text-[#888] hover:text-[#444]'
                      }`}
                      title="Table View"
                    >
                      <FaThList className="w-3 h-3" />
                      <span className="hidden sm:inline">Table</span>
                    </button>
                    <button
                      onClick={() => setViewMode('kanban')}
                      className={`flex items-center space-x-1.5 px-3 py-1.5 text-[13px] font-medium rounded-md transition-all duration-150 ${
                        viewMode === 'kanban'
                          ? 'bg-white text-[#171717] shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-[#eaeaea]'
                          : 'text-[#888] hover:text-[#444]'
                      }`}
                      title="Kanban View"
                    >
                      <FaTh className="w-3 h-3" />
                      <span className="hidden sm:inline">Kanban</span>
                    </button>
                  </div>

                  {/* Add Action Item */}
                    {!readOnly && (
                    <button
                      onClick={() => setShowAddActionItem(true)}
                      className="flex items-center space-x-2 bg-[#171717] text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-[#333] transition-colors duration-150"
                    >
                      <FaPlus className="w-3 h-3" />
                      <span>Add Action Item</span>
                    </button>
                    )}
                </div>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #eaeaea' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        data-testid="action-items-filter-status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Status</option>
                        <option value="NOT_STARTED">Not Started</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="ON_HOLD">On Hold</option>
                        <option value="COMPLETED">Completed</option>
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        data-testid="action-items-filter-user"
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Owners</option>
                        {projectData?.users?.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        data-testid="action-items-filter-priority"
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Priority</option>
                        <option value="LOW">Low</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="HIGH">High</option>
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        data-testid="action-items-filter-risk"
                        value={riskFilter}
                        onChange={(e) => setRiskFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Risk</option>
                        <option value="NONE">None</option>
                        <option value="LOW">Low</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="HIGH">High</option>
                        <option value="CRITICAL">Critical</option>
                      </select>
                    </div>
                  </div>

                  {/* Active Filters & Clear */}
                  {(statusFilter !== 'all' || userFilter !== 'all' || priorityFilter !== 'all' || riskFilter !== 'all' || !isDefaultItemSort(sortKeys)) && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-[11px] text-[#888]">Active filters:</span>
                        {!isDefaultItemSort(sortKeys) && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#444] border border-[#eaeaea] rounded-lg">
                            Sort: {sortKeys.map((key) => `${SORT_FIELD_LABELS[key.field] ?? key.field} ${key.direction === 'asc' ? '↑' : '↓'}`).join(', ')}
                            <button onClick={() => setSortKeys(DEFAULT_ITEM_SORT)} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                        {statusFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#444] border border-[#eaeaea] rounded-lg">
                            Status: {STATUS_LABELS[statusFilter] ?? statusFilter}
                            <button onClick={() => setStatusFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                        {userFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#444] border border-[#eaeaea] rounded-lg">
                            User: {projectData?.users?.find(u => u.id === userFilter)?.first_name}
                            <button onClick={() => setUserFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                        {priorityFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#444] border border-[#eaeaea] rounded-lg">
                            Priority: {priorityFilter}
                            <button onClick={() => setPriorityFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setUserFilter('all');
                          setPriorityFilter('all');
                          setRiskFilter('all');
                          setSortKeys(DEFAULT_ITEM_SORT);
                        }}
                        className="text-[11px] text-[#888] hover:text-[#171717] font-medium transition-colors duration-150"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header - Only show for table view */}
            {viewMode === 'table' && (
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                {/* Left: flexible name area */}
                <div className="flex items-center flex-1 min-w-0">
                  <div className="mr-4 p-1"><div className="w-3"></div></div>
                  <div className="mr-4"><div className="w-2"></div></div>
                  <div className="flex-1" role="columnheader">Action Item</div>
                </div>
                {/* Right: fixed columns — widths must match ActionItemRow */}
                <div className="flex items-center flex-shrink-0 gap-2">
                  <div className="w-[82px]" role="columnheader">
                    <button
                      data-testid="action-items-sort-status"
                      onClick={(event) => toggleSort('status', event.shiftKey)}
                      className="flex items-center gap-1 text-xs text-[#444] hover:text-[#171717]"
                    >
                      Status&nbsp;{sortIndicator(sortKeys, 'status')}
                    </button>
                  </div>
                  <div className="w-11 text-center" role="columnheader">Owner</div>
                  <div className="w-11 text-center" role="columnheader">Assignee</div>
                  <div className="w-[100px]" role="columnheader">
                    <button
                      data-testid="action-items-sort-due"
                      onClick={(event) => toggleSort('due_date', event.shiftKey)}
                      className="flex items-center gap-1 text-xs text-[#444] hover:text-[#171717]"
                    >
                      Due&nbsp;{sortIndicator(sortKeys, 'due_date')}
                    </button>
                  </div>
                  <div className="w-[100px]" role="columnheader">
                    <button
                      data-testid="action-items-sort-created"
                      onClick={(event) => toggleSort('created_at', event.shiftKey)}
                      className="flex items-center gap-1 text-xs text-[#444] hover:text-[#171717]"
                    >
                      Created&nbsp;{sortIndicator(sortKeys, 'created_at')}
                    </button>
                  </div>
                  <div className="w-[62px]" role="columnheader">
                    <button
                      data-testid="action-items-sort-priority"
                      onClick={(event) => toggleSort('priority', event.shiftKey)}
                      className="flex items-center gap-1 text-xs text-[#444] hover:text-[#171717]"
                    >
                      Priority&nbsp;{sortIndicator(sortKeys, 'priority')}
                    </button>
                  </div>
                  <div className="w-[62px]" role="columnheader">
                    <button
                      data-testid="action-items-sort-risk"
                      onClick={(event) => toggleSort('risk', event.shiftKey)}
                      className="flex items-center gap-1 text-xs text-[#444] hover:text-[#171717]"
                    >
                      Risk&nbsp;{sortIndicator(sortKeys, 'risk')}
                    </button>
                  </div>
                  <div className="w-7" role="columnheader"></div>
                  <div className="w-6" role="columnheader"></div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Area - Table or Kanban */}
      <div
        className="max-w-[1280px] mx-auto px-0"
        style={{
          position: 'relative',
          zIndex: 5,
        }}
      >
        {/* Loading State */}
        {isLoading && actionItems.length === 0 && (
          <LoadingSpinner text="Loading action items..." />
        )}

        {/* Table View */}
        {!isLoading && viewMode === 'table' && (
        <div className="bg-white">
          {/* Action Item Rows */}
          <div>
            {sortedActionItems.map((actionItem) => (
              <ActionItemRow
                key={actionItem.id}
                actionItem={actionItem}
                projectData={projectData}
                onSubmit={updateActionItemHandler}
                onDelete={deleteActionItemHandler}
                onProjectUpdate={onProjectUpdate}
                isHighlighted={highlightedItemId === actionItem.id}
                canDelete={currentUserId === actionItem.owner_id}
                isExpanded={expandedItemId === actionItem.id}
                onToggleExpand={() => setExpandedItemId(prev => prev === actionItem.id ? null : actionItem.id!)}
              />
            ))}
          </div>
        </div>
        )}

        {/* Kanban View */}
        {!isLoading && viewMode === 'kanban' && projectId && (
          <div className="bg-[#fafafa]/30 rounded-xl py-4 mt-4">
            <ActionItemsKanbanBoard
              projectId={projectId}
              projectData={projectData}
              filteredItems={filteredActionItems}
              onUpdateItem={updateActionItemHandler}
              onProjectUpdate={onProjectUpdate}
            />
          </div>
        )}

        {!isLoading && viewMode === 'table' && (
          <Pagination
            page={actionItemsPagination.page || page}
            size={actionItemsPagination.size || pageSize}
            total={actionItemsPagination.total}
            onPageChange={setPage}
            onSizeChange={handleSizeChange}
          />
        )}

        {/* Empty State */}
        {!isLoading && filteredActionItems.length === 0 && (
          <div className="text-center py-16">
            <div className="text-[#ccc] mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-[15px] font-medium text-[#171717] mb-2">No action items found</h3>
            <p className="text-[13px] text-[#888] mb-8">
              {searchQuery || statusFilter !== 'all' || userFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Get started by creating your first action item.'
              }
            </p>
              <button
                onClick={() => setShowAddActionItem(true)}
                className="bg-white border border-[#eaeaea] text-[#444] px-6 py-2 rounded-lg text-[13px] font-medium hover:bg-[#fafafa] hover:border-[#ddd] transition-colors duration-150"
              >
                Create First Action Item
              </button>
          </div>
        )}
      </div>

      {/* Add Action Item Modal */}
      {showAddActionItem && projectId && (
        <AddActionItemModal
          isOpen={showAddActionItem}
          onClose={() => setShowAddActionItem(false)}
          onAddItem={handleAddActionItem}
          projectData={projectData}
          users={projectData?.users || []}
        />
      )}
    </div>
  );
}; 